import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { zip } from 'fflate';
import {
  Download, Loader2, CheckCircle2, AlertCircle, Sparkles, Megaphone, Upload, X,
} from 'lucide-react';
import lockupUrl from '../assets/inviter/lockup.png';
import phone5Url from '../assets/inviter/phone-5.png';
import phone8Url from '../assets/inviter/phone-8.png';
import ref45Url from '../assets/inviter/ref-45.png';
import refStoryUrl from '../assets/inviter/ref-story.png';

const API_BASE = 'https://api.glowytics.com';

// Formatlar — Gemini imageConfig oranları.
const FORMATS = [
  {
    key: '4:5', label: '4:5 · IG Feed', default: true,
    layout:
      'PORTRAIT 4:5 LAYOUT: the logo lock-up sits in the TOP-LEFT corner. The headline is set LEFT-ALIGNED on the ' +
      'left half of the frame, vertically around the middle, with the sub-headline directly beneath it. The hand ' +
      'holding the phone occupies the RIGHT side of the frame, large, tilted slightly, extending past the bottom-right ' +
      'edge. The two app-store badges sit side by side in the BOTTOM-LEFT corner, below the headline block.',
  },
  {
    key: '9:16', label: '9:16 · Story / Reels',
    layout:
      'VERTICAL 9:16 LAYOUT: the logo lock-up sits in the TOP-LEFT corner. Directly below it the headline runs ' +
      'LEFT-ALIGNED across the upper third of the frame, with the sub-headline beneath it. The hand holding the phone ' +
      'is CENTRED in the lower two-thirds, large and upright, rising from the bottom of the frame. The two app-store ' +
      'badges sit side by side in the BOTTOM-LEFT corner.',
  },
  {
    key: '1:1', label: '1:1 · Kare',
    layout:
      'SQUARE 1:1 LAYOUT: the logo lock-up sits in the TOP-LEFT corner. The headline is LEFT-ALIGNED on the left half ' +
      'with the sub-headline beneath it. The hand holding the phone occupies the RIGHT half, tilted slightly and ' +
      'cropped by the bottom edge. The two app-store badges sit side by side in the BOTTOM-LEFT corner.',
  },
];

// Arka plan / renk dünyası
const PALETTES = [
  {
    id: 'purple', label: 'Mor (marka)', swatch: 'linear-gradient(135deg,#351079,#6325B8)',
    prompt: 'A rich, saturated purple brand background: deep violet (#351079) at the edges and corners, blending into a ' +
      'brighter vivid purple (#6325B8) glow that radiates from behind the phone. Perfectly smooth studio gradient, ' +
      'subtle soft vignette, no texture, no pattern, no noise.',
  },
  {
    id: 'gradient', label: 'Marka gradyanı', swatch: 'linear-gradient(135deg,#E3330A,#FF6600,#FFCC00,#66FFCC,#00CCFF)',
    prompt: 'A vibrant brand-gradient background sweeping from warm orange-red through yellow into mint and cyan, ' +
      'smooth and glossy, with a soft radial glow behind the phone. Bold and energetic but clean — no texture, no ' +
      'pattern, no noise.',
  },
  {
    id: 'dark', label: 'Koyu premium', swatch: 'linear-gradient(135deg,#0B0B14,#241A3D)',
    prompt: 'A deep near-black premium background with a subtle dark violet glow behind the phone, cinematic and ' +
      'high-contrast so the white text and the phone screen pop. Smooth, clean, no texture or noise.',
  },
  {
    id: 'light', label: 'Açık / ferah', swatch: 'linear-gradient(135deg,#F5F2FF,#E4DBFF)',
    prompt: 'A bright, airy off-white to soft lilac background with a gentle glow behind the phone. Clean, modern and ' +
      'premium. On this light background all headline and sub-headline text must be near-black (#0B0B0F) instead of ' +
      'white, so it stays readable.',
  },
];

// Telefonun nasıl görüneceği
const PHONE_MODES = [
  {
    id: 'hand', label: 'Elde tutulan telefon',
    prompt: 'A realistic human hand holds the phone, gripping it naturally from the side and below with correct ' +
      'anatomy — five fingers, believable knuckles and nails, natural skin tone and soft realistic shadows. The hand ' +
      'and forearm enter the frame from the bottom edge. The phone is held slightly tilted, screen facing the viewer ' +
      'and fully legible, never obscured by the fingers.',
  },
  {
    id: 'float', label: 'Havada duran cihaz',
    prompt: 'The phone floats cleanly in the frame with no hand, tilted slightly in a subtle 3D perspective, with a ' +
      'soft realistic drop shadow and a gentle glow behind it. Premium product-render look, screen fully visible and ' +
      'perfectly legible.',
  },
  {
    id: 'two', label: 'İki telefon yan yana',
    prompt: 'Two phones are shown overlapping side by side, the front one slightly lower and tilted, the second ' +
      'behind and offset, both floating with soft realistic shadows and a glow behind them. Both screens are fully ' +
      'visible and legible.',
  },
];

// Diller — metin doğruluğu talimatı dile göre değişiyor.
const LANGS = [
  {
    id: 'tr', label: 'Türkçe',
    accuracy:
      'THE COPY IS TURKISH. Render it EXACTLY as given, character for character, preserving every Turkish diacritic ' +
      '(ı, İ, ş, Ş, ğ, Ğ, ü, Ü, ö, Ö, ç, Ç, â). Note that Turkish has a dotless "ı" and a dotted capital "İ" — do not ' +
      'substitute the English "i"/"I" for them. Do not translate the copy into English, do not paraphrase it, do not ' +
      '"correct" it.',
  },
  {
    id: 'en', label: 'English',
    accuracy:
      'THE COPY IS ENGLISH. Render it EXACTLY as given, character for character, with correct English spelling and ' +
      'clean apostrophes. Do not translate it into another language, do not paraphrase it, do not rewrite it.',
  },
];

// Hazır metin setleri — dil bazlı.
const COPY_PRESETS = {
  tr: [
    { id: 'tr1', headline: 'Davetiyeden anıya,\nhepsi tek uygulamada', subline: 'Yapay zekâ ile tasarla, davet et, anılarını sakla.' },
    { id: 'tr2', headline: 'Düğün davetiyeniz\n2 dakikada hazır', subline: '500+ şablon, canlı katılım takibi, ortak fotoğraf galerisi.' },
    { id: 'tr3', headline: 'Daveti gönder,\nanıyı birlikte yaşa', subline: 'Misafirlerin çektiği her kare tek yerde toplansın.' },
    { id: 'tr4', headline: 'Etkinlik bitti,\nhikâye başlıyor', subline: 'Tüm fotoğraflar tek dokunuşla sinematik anı filmine dönüşsün.' },
  ],
  en: [
    { id: 'en1', headline: 'From invite to memory,\nall in one app', subline: 'Design with AI, invite your guests, keep every memory.' },
    { id: 'en2', headline: 'Your wedding invite,\nready in 2 minutes', subline: '500+ templates, live RSVP tracking and a shared photo gallery.' },
    { id: 'en3', headline: 'Send the invite.\nLive the moment.', subline: 'Every photo your guests take, collected in one place.' },
    { id: 'en4', headline: 'The event ends,\nthe story begins', subline: 'Turn every photo into a cinematic memory film in one tap.' },
  ],
};

const PHONES = [
  { id: 'p5', label: 'Telefon 1 · Davetiye ekranı', url: phone5Url },
  { id: 'p8', label: 'Telefon 2 · Galeri ekranı', url: phone8Url },
];

const STYLE_REFS = [
  { id: 'none', label: 'Yok', url: null },
  { id: 'r45', label: 'Örnek 4:5', url: ref45Url },
  { id: 'rstory', label: 'Örnek Story', url: refStoryUrl },
];

// Tüm kreatifi (metin + logo + rozetler dahil) Gemini'nin üretmesi için temel talimat.
const BASE = (
  'You are a senior performance-marketing art director. Produce a FINISHED, ready-to-publish mobile app install ad ' +
  'creative for an app called "inviter" — a digital invitation app for weddings, birthdays and events. ' +
  'This is the FINAL asset: render EVERYTHING inside the image, including all typography, the logo lock-up and the ' +
  'app-store badges. Nothing will be added afterwards.\n\n' +

  'BRAND LOGO LOCK-UP — THE FIRST ATTACHED REFERENCE IMAGE IS THE FINISHED LOGO. Copy it into the top-left corner ' +
  'EXACTLY as it appears: same artwork, same proportions, same spacing, same alignment. Do NOT redraw, redesign, ' +
  'restyle, recolour or reinterpret the icon inside the black tile — treat that icon as a fixed piece of artwork to ' +
  'be reproduced faithfully, not as something to illustrate from scratch. Do not add a second logo anywhere.\n\n' +

  'LOGO SIZE — THIS IS A HARD CONSTRAINT AND THE MOST COMMON MISTAKE: the logo lock-up must be SMALL and modest. Its ' +
  'total width must be at most 30% of the image width, and its total height at most 7% of the image height. It is a ' +
  'discreet brand mark tucked into the top-left corner with comfortable margin around it — it is NEVER a hero ' +
  'element, never oversized, never dominating the frame. The headline must be visually much more prominent than the ' +
  'logo. If in doubt, make the logo smaller.\n\n' +

  'STORE BADGES: in the bottom-left corner place the two official app-store download badges side by side — the Apple ' +
  '"Download on the App Store" badge with the Apple logo, and the "GET IT ON Google Play" badge with the coloured ' +
  'Play triangle. Render them as clean black rounded rectangles with crisp white text, correctly proportioned and ' +
  'perfectly legible, exactly as the real badges look.\n\n' +

  'STRICTLY NO CALL-TO-ACTION BUTTON: do not draw any button, pill, "Download"/"İndir"/"Get started" control, arrow, ' +
  'banner or any other clickable-looking element anywhere in the creative. The only interface-like elements allowed ' +
  'are the two store badges and whatever appears inside the phone screen itself.\n\n' +

  'TYPOGRAPHY: modern heavy geometric sans-serif, tight tracking, generous line spacing. The headline is the largest ' +
  'element after the phone. Keep the composition clean and premium with real breathing room — this must look like a ' +
  'creative made by a top mobile-growth studio, not a cluttered template.\n\n' +

  'TEXT ACCURACY IS CRITICAL: do not drop or invent letters, and do not add any other words, captions, labels, ' +
  'taglines, watermarks or URLs anywhere in the image. Every character must be crisp and correctly formed.\n\n' +

  'Output a single, complete, high-resolution advertising image.'
);

function buildPrompt({ format, palette, phoneMode, headline, subline, phoneRefLabel, hasStyleRef, extra, lang }) {
  const parts = [BASE, `COPY LANGUAGE: ${lang.accuracy}`];
  // Lock-up referansı beyaz wordmark'lı; açık zeminde okunmaz.
  parts.push(palette.id === 'light'
    ? 'LOGO ON LIGHT BACKGROUND: keep the black icon tile exactly as in the reference, but render the "inviter" '
      + 'wordmark next to it in near-black (#0B0B0F) instead of white, so it stays readable on the light background.'
    : 'LOGO COLOUR: keep the "inviter" wordmark WHITE, exactly as in the reference — never black or dark on this '
      + 'dark background.');
  parts.push(`BACKGROUND: ${palette.prompt}`);
  parts.push(`DEVICE: ${phoneMode.prompt}`);
  parts.push(
    `PHONE SCREEN: an attached reference image shows the actual inviter app screen (${phoneRefLabel}). Reproduce that ` +
    'screen inside the phone as faithfully as you can — same layout, same cards, same colours and the same overall ' +
    'structure — so it is recognisably this app. Keep the screen bright, sharp and undistorted. Any small interface ' +
    'wording inside the screen must be spelled correctly and legibly, never garbled or invented.',
  );
  if (hasStyleRef) {
    parts.push(
      'STYLE REFERENCE: one attached image is a previously approved creative. Match its overall composition, colour ' +
      'world, typographic scale and premium feel — but use the new copy given below, not the copy in that reference.',
    );
  }
  parts.push(format.layout);
  parts.push(
    `HEADLINE (render exactly, keeping the line break):\n"${headline}"\n\n` +
    `SUB-HEADLINE (render exactly, smaller and lighter than the headline):\n"${subline}"`,
  );
  if (extra) parts.push(`EXTRA DIRECTION: ${extra}`);
  return parts.join('\n\n');
}

async function urlToPayload(url, maxEdge = 1400) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = () => rej(new Error(`Görsel yüklenemedi: ${String(url).slice(-40)}`));
    i.src = url;
  });
  const iw = img.naturalWidth, ih = img.naturalHeight;
  const s = Math.min(1, maxEdge / Math.max(iw, ih));
  const cv = document.createElement('canvas');
  cv.width = Math.max(1, Math.round(iw * s));
  cv.height = Math.max(1, Math.round(ih * s));
  const c = cv.getContext('2d');
  // Şeffaf PNG'yi (telefon mockup'ı) JPEG'e çevirirken siyah zemin oluşmasın.
  c.fillStyle = '#ffffff';
  c.fillRect(0, 0, cv.width, cv.height);
  c.drawImage(img, 0, 0, cv.width, cv.height);
  const dataUrl = cv.toDataURL('image/jpeg', 0.92);
  return { dataUrl, payload: { mimeType: 'image/jpeg', data: dataUrl.split(',')[1] || '' } };
}

async function callGemini({ payloads, aspect, prompt, model, brandId }) {
  const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
    body: JSON.stringify({
      model,
      aspectRatio: aspect,
      temperature: 0.75,
      maxOutputTokens: 8192,
      messages: [{ role: 'user', content: prompt, images: payloads || [] }],
    }),
  });
  const txt = await resp.text();
  let json;
  try { json = JSON.parse(txt); } catch { json = { message: txt }; }
  if (!resp.ok) {
    const d = json?.details;
    const ds = !d ? '' : (typeof d === 'string' ? d.slice(0, 300) : (() => { try { return JSON.stringify(d).slice(0, 300); } catch { return ''; } })());
    throw new Error([json?.error, ds].filter(Boolean).join(' — ') || resp.statusText);
  }
  const images = Array.isArray(json?.images) ? json.images : [];
  if (!images.length) {
    const mt = (json?.message || '').trim();
    throw new Error(mt ? `Model görsel yerine yazı döndü: "${mt.slice(0, 160)}"` : 'Model görsel üretmedi.');
  }
  return images[0];
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function uid() { return Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4); }
function stamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
}

export default function InviterAds() {
  const [formatKeys, setFormatKeys] = useState(() => FORMATS.filter((f) => f.default).map((f) => f.key));
  const [paletteId, setPaletteId] = useState('purple');
  const [phoneModeId, setPhoneModeId] = useState('hand');
  const [phoneId, setPhoneId] = useState('p5');
  const [styleRefId, setStyleRefId] = useState('rstory');
  const [langId, setLangId] = useState('tr');
  const [headline, setHeadline] = useState(COPY_PRESETS.tr[0].headline);
  const [subline, setSubline] = useState(COPY_PRESETS.tr[0].subline);
  const [extra, setExtra] = useState('');
  const [variants, setVariants] = useState(2);
  const [model, setModel] = useState('gemini-3-pro-image-preview');
  const [customPhone, setCustomPhone] = useState(null);

  const [running, setRunning] = useState(false);
  const [zipBuilding, setZipBuilding] = useState(false);
  const [outputs, setOutputs] = useState([]);
  const [error, setError] = useState('');
  const [logoPayload, setLogoPayload] = useState(null);
  const fileRef = useRef(null);

  const palette = useMemo(() => PALETTES.find((p) => p.id === paletteId) || PALETTES[0], [paletteId]);
  const phoneMode = useMemo(() => PHONE_MODES.find((p) => p.id === phoneModeId) || PHONE_MODES[0], [phoneModeId]);
  const styleRef = useMemo(() => STYLE_REFS.find((s) => s.id === styleRefId) || STYLE_REFS[0], [styleRefId]);
  const lang = useMemo(() => LANGS.find((l) => l.id === langId) || LANGS[0], [langId]);
  const presets = COPY_PRESETS[langId] || COPY_PRESETS.tr;

  // Dil değişince o dilin ilk hazır metnine geç (elle yazılmış metin karışık dilde kalmasın).
  const switchLang = (id) => {
    if (id === langId) return;
    setLangId(id);
    const p = (COPY_PRESETS[id] || COPY_PRESETS.tr)[0];
    setHeadline(p.headline); setSubline(p.subline);
  };
  const activeFormats = useMemo(() => FORMATS.filter((f) => formatKeys.includes(f.key)), [formatKeys]);

  const brandId = useMemo(() => {
    try { return localStorage.getItem('currentBrandId') || localStorage.getItem('selectedBrandId') || ''; }
    catch (_) { return ''; }
  }, []);

  // Logo referansını bir kez hazırla.
  useEffect(() => {
    let alive = true;
    urlToPayload(lockupUrl, 1200)
      .then((p) => { if (alive) setLogoPayload(p); })
      .catch((e) => setError(`Logo yüklenemedi: ${e.message}`));
    return () => { alive = false; };
  }, []);

  const toggleFormat = (k) => setFormatKeys((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const onPhoneFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const url = URL.createObjectURL(f);
    try {
      const p = await urlToPayload(url);
      setCustomPhone({ ...p, name: f.name });
      setPhoneId('custom');
    } catch (err) { setError(`Telefon görseli yüklenemedi: ${err.message}`); }
    finally { URL.revokeObjectURL(url); }
  };

  const generate = useCallback(async () => {
    if (running) return;
    if (!activeFormats.length) { setError('En az bir format seç.'); return; }
    if (!logoPayload) { setError('Logo referansı henüz hazır değil, bir saniye.'); return; }
    if (!headline.trim()) { setError('Başlık boş olamaz.'); return; }
    setError(''); setRunning(true);

    try {
      // Referans görseller: logo → telefon ekranı → (ops.) stil örneği
      const phone = phoneId === 'custom' ? customPhone : PHONES.find((p) => p.id === phoneId);
      const phonePayload = phoneId === 'custom'
        ? customPhone
        : await urlToPayload(phone.url);
      const stylePayload = styleRef.url ? await urlToPayload(styleRef.url, 1100) : null;
      const payloads = [logoPayload.payload, phonePayload.payload];
      if (stylePayload) payloads.push(stylePayload.payload);

      const jobs = [];
      for (const f of activeFormats) {
        for (let v = 1; v <= variants; v++) jobs.push({ id: uid(), format: f, variant: v });
      }
      setOutputs(jobs.map((j) => ({
        id: j.id, formatKey: j.format.key, variant: j.variant,
        status: 'pending', dataUrl: null, error: null,
      })));

      for (const j of jobs) {
        try {
          const prompt = buildPrompt({
            format: j.format, palette, phoneMode, headline: headline.trim(), subline: subline.trim(),
            phoneRefLabel: phoneId === 'custom' ? (customPhone?.name || 'yüklenen ekran') : phone.label,
            hasStyleRef: !!stylePayload, extra: extra.trim(), lang,
          });
          const img = await callGemini({ payloads, aspect: j.format.key, prompt, model, brandId });
          const dataUrl = `data:${img.mimeType || 'image/png'};base64,${img.data}`;
          setOutputs((prev) => prev.map((o) => o.id === j.id
            ? { ...o, status: 'done', dataUrl, mimeType: img.mimeType, data: img.data } : o));
        } catch (e) {
          setOutputs((prev) => prev.map((o) => o.id === j.id
            ? { ...o, status: 'error', error: e.message || String(e) } : o));
        }
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally { setRunning(false); }
  }, [running, activeFormats, logoPayload, headline, subline, phoneId, customPhone, styleRef,
    palette, phoneMode, extra, variants, model, brandId, lang]);

  const nameFor = (o) => `inviter_${langId}_${paletteId}_${o.formatKey.replace(':', 'x')}_v${o.variant}.png`;

  const downloadOne = (o) => {
    if (!o?.dataUrl) return;
    const a = document.createElement('a');
    a.href = o.dataUrl; a.download = nameFor(o); a.click();
  };

  const downloadZip = useCallback(async () => {
    const done = outputs.filter((o) => o.status === 'done' && o.data);
    if (!done.length) return;
    setZipBuilding(true);
    try {
      const files = {};
      const counts = {};
      for (const o of done) {
        let n = nameFor(o);
        counts[n] = (counts[n] || 0) + 1;
        if (counts[n] > 1) n = n.replace(/\.png$/, `_${counts[n]}.png`);
        files[n] = b64ToBytes(o.data);
      }
      const zipped = await new Promise((res, rej) => zip(files, { level: 6 }, (e, d) => e ? rej(e) : res(d)));
      const url = URL.createObjectURL(new Blob([zipped], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = `inviter-ads_${stamp()}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(`ZIP oluşturulamadı: ${e.message || e}`); }
    finally { setZipBuilding(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputs, paletteId]);

  const doneCount = outputs.filter((o) => o.status === 'done').length;
  const jobCount = activeFormats.length * variants;

  const card = 'rounded-2xl border border-gray-200 bg-white p-4';
  const chip = (on) => `rounded-lg border px-3 py-1.5 text-sm font-medium transition ${on
    ? 'border-violet-400 bg-violet-50 text-violet-800 ring-1 ring-violet-300'
    : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl text-white"
          style={{ background: 'linear-gradient(135deg,#351079,#6325B8)' }}>
          <Megaphone className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            inviter Install Reklam Stüdyosu <span className="text-sm font-normal text-violet-600">· Nano Banana</span>
          </h1>
          <p className="text-sm text-gray-500">
            Kreatifin tamamı — arka plan, logo, başlık, store rozetleri — tek promptla Gemini'den çıkar. CTA butonu yok.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {/* 1 · Metin */}
      <div className={card}>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">1 · Metin</span>
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 text-xs">
            {LANGS.map((l) => (
              <button key={l.id} onClick={() => switchLang(l.id)}
                className={`px-3 py-1.5 font-medium ${langId === l.id ? 'bg-violet-100 text-violet-800' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-3 flex flex-wrap gap-2">
          {presets.map((c) => (
            <button key={c.id} onClick={() => { setHeadline(c.headline); setSubline(c.subline); }}
              className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-500 hover:bg-gray-50">
              {c.headline.replace('\n', ' ').slice(0, 34)}…
            </button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-gray-500">Ana başlık <span className="text-gray-400">(satır sonu = alt satır)</span></label>
            <textarea value={headline} onChange={(e) => setHeadline(e.target.value)} rows={2}
              className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Alt başlık</label>
            <textarea value={subline} onChange={(e) => setSubline(e.target.value)} rows={2}
              className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none" />
          </div>
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          Prompt modele metni harfi harfine basmasını söylüyor; Türkçe seçiliyken noktasız <b>ı</b> ve noktalı <b>İ</b>
          ayrıca vurgulanıyor. Yine de her çıktıda yazımı gözle kontrol et — model harf kaydırabiliyor.
        </p>
      </div>

      {/* 2 · Görsel dünya */}
      <div className={card}>
        <div className="mb-3 text-sm font-semibold text-gray-700">2 · Görsel dünya</div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Arka plan</span>
          {PALETTES.map((p) => (
            <button key={p.id} onClick={() => setPaletteId(p.id)} className={`${chip(paletteId === p.id)} inline-flex items-center gap-2`}>
              <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: p.swatch }} />
              {p.label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Cihaz</span>
          {PHONE_MODES.map((p) => (
            <button key={p.id} onClick={() => setPhoneModeId(p.id)} className={chip(phoneModeId === p.id)}>{p.label}</button>
          ))}
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-medium text-gray-500">Telefon ekranı (referans)</div>
            <div className="flex flex-wrap items-start gap-3">
              {PHONES.map((p) => (
                <button key={p.id} onClick={() => setPhoneId(p.id)}
                  className={`overflow-hidden rounded-lg border-2 p-1 transition ${phoneId === p.id ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <img src={p.url} alt={p.label} className="h-24 w-auto object-contain" />
                  <div className="mt-1 max-w-[7rem] truncate text-[10px] text-gray-500">{p.label}</div>
                </button>
              ))}
              {customPhone && (
                <button onClick={() => setPhoneId('custom')}
                  className={`relative overflow-hidden rounded-lg border-2 p-1 transition ${phoneId === 'custom' ? 'border-violet-500 bg-violet-50' : 'border-gray-200'}`}>
                  <img src={customPhone.dataUrl} alt="özel" className="h-24 w-auto object-contain" />
                  <div className="mt-1 max-w-[7rem] truncate text-[10px] text-gray-500">{customPhone.name}</div>
                  <span onClick={(ev) => { ev.stopPropagation(); setCustomPhone(null); setPhoneId('p5'); }}
                    className="absolute right-0.5 top-0.5 rounded-full bg-white/90 p-0.5 text-gray-400 shadow hover:text-red-600">
                    <X className="h-3 w-3" />
                  </span>
                </button>
              )}
              <button onClick={() => fileRef.current?.click()}
                className="flex h-24 w-20 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 hover:border-violet-300 hover:text-violet-500">
                <Upload className="h-4 w-4" /><span className="text-[10px]">Ekran yükle</span>
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoneFile} />
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium text-gray-500">Stil referansı <span className="text-gray-400">(beğendiğin örnek)</span></div>
            <div className="flex flex-wrap gap-2">
              {STYLE_REFS.map((s) => (
                <button key={s.id} onClick={() => setStyleRefId(s.id)}
                  className={`overflow-hidden rounded-lg border-2 p-1 transition ${styleRefId === s.id ? 'border-violet-500 bg-violet-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  {s.url
                    ? <img src={s.url} alt={s.label} className="h-24 w-auto object-contain" />
                    : <div className="flex h-24 w-16 items-center justify-center text-[11px] text-gray-400">Yok</div>}
                  <div className="mt-1 text-[10px] text-gray-500">{s.label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-gray-500">Ekstra yönlendirme (opsiyonel)</label>
          <input value={extra} onChange={(e) => setExtra(e.target.value)}
            placeholder="örn. arka planda hafif konfeti, telefon biraz daha büyük olsun"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none" />
        </div>
      </div>

      {/* 3 · Format & üretim */}
      <div className={card}>
        <div className="mb-3 text-sm font-semibold text-gray-700">3 · Format &amp; üretim</div>
        <div className="flex flex-wrap items-center gap-2">
          {FORMATS.map((f) => (
            <button key={f.key} onClick={() => toggleFormat(f.key)} className={chip(formatKeys.includes(f.key))}>{f.label}</button>
          ))}
          <div className="mx-2 h-6 w-px bg-gray-200" />
          <span className="text-xs text-gray-500">Varyant</span>
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 text-xs">
            {[1, 2, 3, 4].map((n) => (
              <button key={n} onClick={() => setVariants(n)}
                className={`px-2.5 py-1.5 font-medium ${variants === n ? 'bg-violet-100 text-violet-800' : 'bg-gray-50 text-gray-500'}`}>{n}×</button>
            ))}
          </div>
          <div className="mx-2 h-6 w-px bg-gray-200" />
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 text-xs">
            <button onClick={() => setModel('gemini-3-pro-image-preview')}
              className={`px-2.5 py-1.5 font-medium ${model === 'gemini-3-pro-image-preview' ? 'bg-violet-100 text-violet-800' : 'bg-gray-50 text-gray-500'}`}>Nano Banana Pro</button>
            <button onClick={() => setModel('gemini-2.5-flash-image')}
              className={`px-2.5 py-1.5 font-medium ${model === 'gemini-2.5-flash-image' ? 'bg-violet-100 text-violet-800' : 'bg-gray-50 text-gray-500'}`}>Flash (hızlı)</button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={generate} disabled={running || !activeFormats.length || !logoPayload}
            className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#351079,#6325B8)' }}>
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {running ? `Üretiliyor… (${doneCount}/${outputs.length})` : `Üret (${activeFormats.length} format × ${variants} = ${jobCount})`}
          </button>
          {doneCount > 0 && (
            <button onClick={downloadZip} disabled={zipBuilding}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
              {zipBuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              ZIP indir ({doneCount})
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          Referans olarak logo + seçtiğin telefon ekranı (+ stil örneği) modele birlikte gönderilir. Üretim sıralı çalışır.
        </p>
      </div>

      {/* Sonuçlar */}
      {outputs.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {outputs.map((o) => (
            <div key={o.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <div className="truncate text-xs font-medium text-gray-600">{o.formatKey} · v{o.variant}</div>
                {o.status === 'done' && (
                  <button onClick={() => downloadOne(o)} className="text-gray-400 hover:text-violet-600">
                    <Download className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center justify-center bg-gray-100"
                style={{ aspectRatio: o.formatKey.replace(':', ' / ') }}>
                {o.status === 'pending' && <Loader2 className="h-6 w-6 animate-spin text-gray-300" />}
                {o.status === 'error' && (
                  <div className="flex flex-col items-center gap-1 px-3 text-center">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                    <div className="text-[11px] text-red-500">{o.error}</div>
                  </div>
                )}
                {o.status === 'done' && <img src={o.dataUrl} alt="kreatif" className="h-full w-full object-contain" />}
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px]">
                {o.status === 'done' && <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /><span className="text-gray-400">Hazır</span></>}
                {o.status === 'pending' && <span className="text-gray-400">Sırada…</span>}
                {o.status === 'error' && <span className="text-red-400">Hata</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {outputs.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-gray-200 py-12 text-center text-gray-400">
          <Megaphone className="h-8 w-8" />
          <div className="text-sm">Metni yaz, formatı seç ve <b>Üret</b>'e bas.</div>
        </div>
      )}
    </div>
  );
}
